import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Save, Key, Mail, Settings as SettingsIcon, Zap, Eye, EyeOff, CheckCircle, User, Lock } from 'lucide-react';

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('api-keys');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showPasswords, setShowPasswords] = useState({});
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [formData, setFormData] = useState({
    api_keys: {
      deepl_api_key: '',
      n8n_api_key: ''
    },
    email: {
      smtp_host: 'smtp.gmail.com',
      smtp_port: 587,
      smtp_user: '',
      smtp_password: '',
      from_email: 'info@surfmotion.com',
      admin_notification_email: 'info@surfmotion.com'
    },
    system: {
      jwt_secret_key: '',
      cors_origins: '*',
      debug_mode: false
    },
    features: {
      translation_enabled: true,
      n8n_integration_enabled: true,
      email_notifications_enabled: true
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/full');
      const data = response.data;
      
      // Ensure all nested objects exist
      const safeData = {
        api_keys: data.api_keys || { deepl_api_key: '', n8n_api_key: '' },
        email: data.email || {
          smtp_host: 'smtp.gmail.com',
          smtp_port: 587,
          smtp_user: '',
          smtp_password: '',
          from_email: 'info@surfmotion.com',
          admin_notification_email: 'info@surfmotion.com'
        },
        system: data.system || {
          jwt_secret_key: '',
          cors_origins: '*',
          debug_mode: false
        },
        features: data.features || {
          translation_enabled: true,
          n8n_integration_enabled: true,
          email_notifications_enabled: true
        }
      };
      
      setSettings(safeData);
      setFormData(safeData);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await api.put('/settings/full', formData);
      setSuccess('✅ Einstellungen erfolgreich gespeichert!');
      await fetchSettings();
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('❌ Fehler beim Speichern der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (category, field, value) => {
    setFormData({
      ...formData,
      [category]: {
        ...formData[category],
        [field]: value
      }
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setError('Bitte füllen Sie alle Felder aus');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Neue Passwörter stimmen nicht überein');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setError('Neues Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      await api.post('/auth/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      
      setSuccess('✅ Passwort erfolgreich geändert!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'api-keys', label: 'API Keys', icon: Key },
    { id: 'email', label: 'Email Settings', icon: Mail },
    { id: 'system', label: 'System', icon: SettingsIcon },
    { id: 'features', label: 'Features', icon: Zap }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Einstellungen</h1>
        <p className="mt-2 text-gray-600">Verwalten Sie Ihre Systemeinstellungen</p>
      </div>

      {success && (
        <div className="p-4 bg-green-50 text-green-800 rounded-lg flex items-center space-x-2">
          <CheckCircle className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 text-red-800 rounded-lg">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-cyan-600 text-cyan-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'account' && (
            <div className="max-w-2xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <Lock className="w-5 h-5 mr-2 text-cyan-600" />
                Passwort ändern
              </h3>
              
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Aktuelles Passwort
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.currentPass ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      placeholder="Ihr aktuelles Passwort"
                      className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('currentPass')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.currentPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Neues Passwort
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.newPass ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      placeholder="Ihr neues Passwort (min. 6 Zeichen)"
                      className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('newPass')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.newPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Mindestens 6 Zeichen</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Neues Passwort bestätigen
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.confirmPass ? 'text' : 'password'}
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      placeholder="Neues Passwort wiederholen"
                      className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirmPass')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.confirmPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full flex items-center justify-center space-x-2 bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Lock className="w-5 h-5" />
                    <span>{saving ? 'Wird geändert...' : 'Passwort ändern'}</span>
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'api-keys' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  DeepL API Key
                  <span className="text-xs text-gray-500 ml-2">(für automatische Übersetzung)</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.deepl ? 'text' : 'password'}
                    value={formData.api_keys?.deepl_api_key || ''}
                    onChange={(e) => handleChange('api_keys', 'deepl_api_key', e.target.value)}
                    placeholder="Ihr DeepL API Key"
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('deepl')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.deepl ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Kostenlos bei <a href="https://www.deepl.com/pro-api" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">deepl.com/pro-api</a>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  n8n API Key
                  <span className="text-xs text-gray-500 ml-2">(für Webhook-Integration)</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.n8n ? 'text' : 'password'}
                    value={formData.api_keys?.n8n_api_key || ''}
                    onChange={(e) => handleChange('api_keys', 'n8n_api_key', e.target.value)}
                    placeholder="Ihr n8n API Key"
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('n8n')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.n8n ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'email' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
                  <input
                    type="text"
                    value={formData.email?.smtp_host || ''}
                    onChange={(e) => handleChange('email', 'smtp_host', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
                  <input
                    type="number"
                    value={formData.email?.smtp_port || 587}
                    onChange={(e) => handleChange('email', 'smtp_port', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">SMTP User</label>
                <input
                  type="email"
                  value={formData.email?.smtp_user || ''}
                  onChange={(e) => handleChange('email', 'smtp_user', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Password</label>
                <div className="relative">
                  <input
                    type={showPasswords.smtp ? 'text' : 'password'}
                    value={formData.email?.smtp_password || ''}
                    onChange={(e) => handleChange('email', 'smtp_password', e.target.value)}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('smtp')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.smtp ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Absender Email (für Kunden-Bestätigungen)</label>
                  <input
                    type="email"
                    value={formData.email?.from_email || ''}
                    onChange={(e) => handleChange('email', 'from_email', e.target.value)}
                    placeholder="info@surfmotion.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Diese Adresse wird als Absender für Bestätigungsmails an Kunden verwendet</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Admin Benachrichtigungs-Email</label>
                  <input
                    type="email"
                    value={formData.email?.admin_notification_email || ''}
                    onChange={(e) => handleChange('email', 'admin_notification_email', e.target.value)}
                    placeholder="admin@surfmotion.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">An diese Adresse werden Kontakt- und Buchungsanfragen gesendet</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'system' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CORS Origins</label>
                <input
                  type="text"
                  value={formData.system?.cors_origins || '*'}
                  onChange={(e) => handleChange('system', 'cors_origins', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">Automatische Übersetzung</h3>
                  <p className="text-sm text-gray-600">DeepL-Integration</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.features?.translation_enabled || false}
                  onChange={(e) => handleChange('features', 'translation_enabled', e.target.checked)}
                  className="w-5 h-5 text-cyan-600 rounded"
                />
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 bg-cyan-600 text-white px-6 py-2 rounded-lg hover:bg-cyan-700 disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            <span>{saving ? 'Speichere...' : 'Speichern'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;